import type { AdminUserSummary } from '@/api';
import { formatDayLabel } from '@/lib/format';
import {
  type DayBucket,
  buildSignupSeries,
  seriesEndDay,
  summarizeUsers
} from '@/lib/userStats';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { useMemo, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/components/ui/tooltip';

const RANGE_OPTIONS = [
  { label: '7d', days: 7 },
  { label: '30d', days: 30 },
  { label: '90d', days: 90 }
];

const ACTIVE_WINDOW_DAYS = 7;

function StatCard({ label, value }: { label: string; value: number }) {
  return (
    <Card>
      <CardContent className="p-3">
        <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
          {label}
        </div>
        <div className="mt-0.5 font-mono text-xl font-semibold tabular-nums">
          {value.toLocaleString()}
        </div>
      </CardContent>
    </Card>
  );
}

/** Bars sized against the window's peak; empty days keep a hoverable hairline. */
function DayBars({ bars }: { bars: DayBucket[] }) {
  const max = Math.max(...bars.map((bar) => bar.count), 1);
  return (
    <TooltipProvider delayDuration={100}>
      <div className="flex h-24 items-end gap-px">
        {bars.map((bar) => (
          <Tooltip key={bar.day}>
            <TooltipTrigger asChild>
              <div className="flex h-full flex-1 items-end">
                <div
                  className="w-full rounded-t bg-primary/70 hover:bg-primary"
                  style={{
                    height: `${Math.max((bar.count / max) * 100, bar.count ? 3 : 1)}%`
                  }}
                />
              </div>
            </TooltipTrigger>
            <TooltipContent
              side="top"
              className="font-mono tabular-nums text-xs"
            >
              {formatDayLabel(bar.day)} · {bar.count.toLocaleString()} new
            </TooltipContent>
          </Tooltip>
        ))}
      </div>
    </TooltipProvider>
  );
}

/**
 * Population stat cards plus a new-accounts-per-day chart for the admin pane.
 *
 * Derives everything from the user list the pane already fetched, so switching
 * the range costs no request. Counts `users.createdAt`, which covers every
 * provisioning path; accounts with no `users` row yet have no date and show up
 * only in the never-logged-in note.
 */
export default function UsersOverview({
  users
}: {
  users: AdminUserSummary[];
}) {
  const [days, setDays] = useState(30);
  const [open, setOpen] = useState(true);

  const population = useMemo(
    () => summarizeUsers(users, Date.now(), ACTIVE_WINDOW_DAYS),
    [users]
  );
  const series = useMemo(
    () => buildSignupSeries(users, days, seriesEndDay(users, new Date())),
    [users, days]
  );
  const newInWindow = series.reduce((sum, bucket) => sum + bucket.count, 0);

  return (
    <div className="flex flex-col gap-2">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="flex items-center gap-1 text-sm font-medium text-muted-foreground hover:text-foreground"
        aria-expanded={open}
      >
        {open ? (
          <ChevronDown className="h-3 w-3" />
        ) : (
          <ChevronRight className="h-3 w-3" />
        )}
        Overview
      </button>

      {open ? (
        <>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            <StatCard label="Users" value={population.total} />
            <StatCard label={`New · ${days}d`} value={newInWindow} />
            <StatCard
              label={`Active · ${ACTIVE_WINDOW_DAYS}d`}
              value={population.activeRecently}
            />
            <StatCard label="Admins" value={population.admins} />
            <StatCard label="Temp" value={population.temporary} />
            <StatCard label="Disabled" value={population.disabled} />
          </div>

          <Card>
            <CardContent className="p-3">
              <div className="mb-2 flex items-center justify-between gap-2">
                <span className="text-xs font-medium">New accounts</span>
                <div className="flex gap-1">
                  {RANGE_OPTIONS.map((option) => (
                    <Button
                      key={option.days}
                      size="sm"
                      variant={option.days === days ? 'default' : 'outline'}
                      className="h-6 px-2 text-[10px]"
                      onClick={() => setDays(option.days)}
                    >
                      {option.label}
                    </Button>
                  ))}
                </div>
              </div>
              <DayBars bars={series} />
              {series.length ? (
                <div className="mt-1 flex justify-between text-[10px] tabular-nums text-muted-foreground">
                  <span>{formatDayLabel(series[0].day)}</span>
                  <span>{formatDayLabel(series[series.length - 1].day)}</span>
                </div>
              ) : null}
              {population.neverLoggedIn ? (
                <p className="mt-2 text-[10px] text-muted-foreground">
                  {population.neverLoggedIn.toLocaleString()} account
                  {population.neverLoggedIn === 1 ? '' : 's'} have never logged
                  in.
                </p>
              ) : null}
            </CardContent>
          </Card>
        </>
      ) : null}
    </div>
  );
}
